import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import config from '../config';
import { createAuth } from '../Helpers';
import moment from 'moment';

export const reducerName = 'resourceState';

export const getAllUnits = createAsyncThunk(
    'resourceState/getAllUnits',
    async (_, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'GET',
                url: `${config.server}/api/unit`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const getAllBookings = createAsyncThunk(
    'resourceState/getAllBookings',
    async (_, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'GET',
                url: `${config.server}/api/unitbooking`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const createBooking = createAsyncThunk(
    'resourceState/createBooking',
    async ({ unitId, teamId, personId, startTime, endTime }, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'POST',
                url: `${config.server}/api/unitbooking`,
                data: {
                    unitId,
                    teamId,
                    personId,
                    startTime: moment(startTime).format('YYYY-MM-DD HH:mm'),
                    endTime: moment(endTime).format('YYYY-MM-DD HH:mm'),
                },
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            if (error.response?.data === 'double_booking') {
                return rejectWithValue({ doubleBook: true });
            }
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

const initialState = {
    units: [],
    bookings: [],
    createBooking: {
        unitId: '',
        teamId: '',
        personId: null,
        startTime: moment().valueOf(),
        endTime: moment().valueOf(),
    },
    doubleBook: false,
    unitMenuId: false,
    showTeamMenu: false,
    showTimeMenu: false,
    loading: { getUnits: false, getBookings: false, createBooking: false },
    error: null,
};

const resourceSlice = createSlice({
    name: reducerName,
    initialState,
    reducers: {
        setBookingField: (state, action) => {
            state.createBooking[action.payload.field] = action.payload.value;
        },
        setTeamAndBook: (state, action) => {
            const { teamId, personId } = action.payload;
            state.createBooking.teamId = teamId;
            state.createBooking.personId = personId;
        },
        unitMenu: (state, action) => {
            state.unitMenuId = action.payload.visible ? action.payload.unitId : false;
        },
        showTeamMenu: (state, action) => {
            state.showTeamMenu = action.payload;
        },
        showTimeMenu: (state, action) => {
            state.showTimeMenu = action.payload;
            state.doubleBook = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // getAllUnits
            .addCase(getAllUnits.pending, (state) => {
                state.loading.getUnits = true;
                state.error = null;
            })
            .addCase(getAllUnits.fulfilled, (state, action) => {
                state.loading.getUnits = false;
                state.units = action.payload;
            })
            .addCase(getAllUnits.rejected, (state, action) => {
                state.loading.getUnits = false;
                state.error = action.payload || 'An error occurred';
            })
            // getAllBookings
            .addCase(getAllBookings.pending, (state) => {
                state.loading.getBookings = true;
                state.error = null;
            })
            .addCase(getAllBookings.fulfilled, (state, action) => {
                state.loading.getBookings = false;
                state.bookings = action.payload;
            })
            .addCase(getAllBookings.rejected, (state, action) => {
                state.loading.getBookings = false;
                state.error = action.payload || 'An error occurred';
            })
            // createBooking
            .addCase(createBooking.pending, (state) => {
                state.loading.createBooking = true;
                state.error = null;
            })
            .addCase(createBooking.fulfilled, (state, action) => {
                state.loading.createBooking = false;
                state.bookings.push(action.payload);
                state.showTeamMenu = false;
                state.unitMenuId = false;
            })
            .addCase(createBooking.rejected, (state, action) => {
                state.loading.createBooking = false;
                if (action.payload?.doubleBook) {
                    state.doubleBook = true;
                } else {
                    state.error = action.payload || 'An error occurred';
                }
            });
    },
});

export const { setBookingField, setTeamAndBook, unitMenu, showTeamMenu, showTimeMenu } = resourceSlice.actions;

export default resourceSlice.reducer;